import { ReactNode, useMemo } from 'react';
import { U21Themes } from 'vendor/material-minimal/models';

import { SCALING_FACTOR } from 'vendor/material-minimal/spacing';

import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeOptions, ThemeProvider } from '@mui/material/styles';
import {
  StyleSheetManager,
  ThemeProvider as SCThemeProvider,
} from 'styled-components';
import StyledEngineProvider from '@mui/material/StyledEngineProvider';

import shape from 'vendor/material-minimal/shape';
import palette from 'vendor/material-minimal/palette';
import typography from 'vendor/material-minimal/typography';
import breakpoints from 'vendor/material-minimal/breakpoints';
import GlobalStyles from 'vendor/material-minimal/globalStyles';
import componentsOverride from 'vendor/material-minimal/overrides';
import { createShadows } from 'vendor/material-minimal/shadows';

import isPropValid from '@emotion/is-prop-valid';

// ----------------------------------------------------------------------
// CUSTOM CHANGES
// - add styled components theme provider
// - removed settings hook
// - defined themes
// - createMuiTheme -> createTheme
// - SCALING_FACTOR constant
// - renamed component
// - changed to named export
// - moved palette mode definition to palette

interface Props {
  children: ReactNode;
  direction?: 'ltr' | 'rtl';
  mode?: U21Themes;
}

export const U21Theme = (props: Props) => {
  const { children, direction = 'ltr', mode = U21Themes.LIGHT } = props;

  const theme = useMemo(() => {
    const isLight = mode === U21Themes.LIGHT;
    const themePalette = isLight ? palette.light : palette.dark;
    const { shadows, customShadows } = createShadows(themePalette);
    const config: ThemeOptions = {
      palette: themePalette,
      typography,
      shape,
      breakpoints,
      spacing: SCALING_FACTOR,
      direction,
      shadows,
      customShadows,
    };
    const muiTheme = createTheme(config);
    muiTheme.components = componentsOverride(muiTheme);
    return muiTheme;
  }, [direction, mode]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles />
        <StyleSheetManager
          enableVendorPrefixes
          // Fix for styled-components v6 https://github.com/styled-components/styled-components/issues/4071#issuecomment-1625465480
          shouldForwardProp={(propName, elementToBeRendered) => {
            return typeof elementToBeRendered === 'string'
              ? isPropValid(propName)
              : true;
          }}
        >
          <SCThemeProvider theme={theme}>{children}</SCThemeProvider>
        </StyleSheetManager>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};
